import { NgIf } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { AssetDto, AssetVersionDto, GalleryAccessLevel, GalleryDto } from '@picflow/types';
import { downloadSizes } from 'src/app/consts';
import { getDownloadUrl } from 'src/app/gallery/lib/download-asset';
import { convertGalleryDownloadSize } from 'src/app/lib/convert-gallery-download-size';
import { devicePixelRatio } from 'src/app/lib/device-pixel-ratio';
import { AppConfigService } from 'src/app/providers/config.service';
import { TranslateService } from 'src/app/services/translate.service';
import { AssetVersionUrlPipe } from 'src/app/shared/pipes/asset-version-url.pipe';
import { ContextMenuComponent } from '../../../shared/components/context-menu/context-menu.component';
import { LoadingIndicatorComponent } from '../../../shared/components/loading-indicator/loading-indicator.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { DownloadSizeSelectionComponent } from '../download-size-selection/download-size-selection.component';

@Component({
  selector: 'app-ios-download-context',
  templateUrl: './ios-download-context.component.html',
  styleUrls: ['./ios-download-context.component.scss'],
  standalone: true,
  imports: [
    ContextMenuComponent,
    ButtonDirective,
    SvgComponent,
    NgIf,
    LoadingIndicatorComponent,
    DownloadSizeSelectionComponent,
  ],
  providers: [AssetVersionUrlPipe],
})
export class IosDownloadContextComponent implements OnChanges {
  @Input() previewWidth: number;
  @Input() accessLevel: GalleryAccessLevel;
  @Input() asset: AssetDto;
  @Input() version: AssetVersionDto;
  @Input() gallery: GalleryDto;
  @Output() download = new EventEmitter();
  scaledPreviewWidth: number;
  width: number;
  assetSrc: string;
  sizeName: string;
  height: number;
  format: string;
  orientation: 'vertical' | 'horizontal';
  assetLoaded = false;
  aspectRatio: number;
  showOptimized: boolean;
  downloadMode: 'long-press' | 'button';

  constructor(
    private versionUrlPipe: AssetVersionUrlPipe,
    private config: AppConfigService,
    private translate: TranslateService
  ) {}

  ngOnChanges() {
    const enabledSizes = convertGalleryDownloadSize(this.gallery.download_media_size);
    if (this.accessLevel === 'edit') {
      this.showOptimized = true;
    } else {
      this.showOptimized = enabledSizes.small && enabledSizes.medium && enabledSizes.large;
    }
    if (this.showOptimized) {
      this.scaledPreviewWidth = Math.round(this.previewWidth * devicePixelRatio());
      this.updateDownloadWidth(this.scaledPreviewWidth);
    } else {
      this.updateDownloadWidth(0);
    }
    this.orientation =
      this.asset.version_data.data.width > this.asset.version_data.data.height ? 'horizontal' : 'vertical';
    this.aspectRatio = this.asset.version_data.data.height / this.asset.version_data.data.width;
  }

  async updateDownloadWidth(size: number) {
    this.width = size;
    this.height = this.calcHeight();
    this.format = this.calcFormat();
    this.assetSrc = await this.calcAssetSrc();
    this.sizeName = await this.calcSizeName();
    this.assetLoaded = false;

    const isSimpleAsset = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(this.format.toLowerCase());
    this.downloadMode = isSimpleAsset ? 'long-press' : 'button';
  }

  private isPreview() {
    return this.width === this.scaledPreviewWidth;
  }

  private async calcAssetSrc() {
    if (this.isPreview()) {
      const version = this.version || this.asset.version_data;
      return this.versionUrlPipe.transform(
        version,
        version.uuid,
        version['status'],
        version.watermark_uuid,
        window.innerWidth,
        false,
        this.asset.watermark_enabled,
        false
      );
    }
    return getDownloadUrl(
      this.version || this.asset.version_data,
      this.asset.type,
      this.width,
      this.config.assetsEndpoint,
      this.asset.watermark_enabled
    );
  }

  private async calcSizeName() {
    if (this.isPreview()) return 'Optimized';
    if (!this.width) return 'Original File';
    const size = downloadSizes.find(size => size.size === this.width);
    if (!size) return 'UNKNOWN';
    return this.translate.get(size.name);
  }

  private calcFormat() {
    if (!this.width) return this.asset.version_data.original_key.split('.').pop().toUpperCase();
    return 'JPG';
  }

  private calcHeight() {
    const aspect = this.asset.version_data.data.height / this.asset.version_data.data.width;
    const width = this.width || this.asset.version_data.data.width;
    return Math.round(width * aspect);
  }
}
